import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useFiltersStore } from '../../store/filtersStore'

/**
 * CentrosSelector - Selector simple de centros de trabajo
 *
 * Permite seleccionar un centro o "Todos los centros".
 * El estado seleccionado se guarda en el store de filtros como:
 *  - null: Todos los centros
 *  - number: Centro específico (IdCentroTrabajo)
 *
 * Props:
 *  - centros: Array de centros desde la API /api/ObtenerCentrosTrabajo
 *    Estructura: [{ IdCentroTrabajo, Nombre, Lat, Long }]
 */
const CentrosSelector = ({ centros = [] }) => {
  const { idCentroTrabajo, setIdCentroTrabajo } = useFiltersStore()

  // Opción "Todos los centros"
  const opcionTodos = { IdCentroTrabajo: null, Nombre: 'Todos los centros' }

  // Lista completa con "Todos" al inicio
  const opciones = [opcionTodos, ...centros]

  // Buscar la opción seleccionada
  const seleccionado = opciones.find((c) => c.IdCentroTrabajo === idCentroTrabajo) || opcionTodos

  return (
    <div className="w-full max-w-sm">
      <Listbox value={seleccionado} onChange={(centro) => setIdCentroTrabajo(centro.IdCentroTrabajo)}>
        {({ open }) => (
          <>
            <div className="relative">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2.5 pl-4 pr-10 text-left border border-gray-300 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary">
                <span className="block truncate font-medium text-sm">{seleccionado.Nombre}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <ChevronDownIcon
                    className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                      open ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {opciones.map((centro) => (
                    <Listbox.Option
                      key={centro.IdCentroTrabajo ?? 'todos'}
                      value={centro}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2.5 px-4 transition-colors duration-150 ${
                          active ? 'bg-gray-100' : ''
                        }`
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center justify-between gap-3">
                          <span
                            className={`text-sm truncate ${
                              selected ? 'font-semibold text-primary' : 'text-gray-900'
                            } ${centro.IdCentroTrabajo === null ? 'font-semibold' : ''}`}
                          >
                            {centro.Nombre}
                          </span>
                          {selected && (
                            <CheckIcon className="h-5 w-5 text-primary flex-shrink-0" strokeWidth={2.5} />
                          )}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}

                  {centros.length === 0 && (
                    <div className="py-4 px-4 text-sm text-gray-500 text-center">
                      No hay centros disponibles
                    </div>
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}

export default CentrosSelector
